import { fromJS } from 'immutable';

import { toJS, spy, setIn, getOriginalIn } from '../../utils/ChangeSpy';
import * as ActionType from '../../actions/company/targetCreate';
import config from '../../config';
import handleApiError from '../decorators/handleApiError';
import { autoNullId, check, resetFieldByOther } from '../decorators/suggest';
import getValidator from '../../utils/getValidator';

const { states, entityTypes } = config.company;

const SCHEMA = {
  type: 'object',
  properties: {
    revenue: {
      title: 'Revenue',
      description: 'should be empty or valid number',
      oneOf: [{ type: 'number', minimum: 0 }, { type: 'string', pattern: /\d*\.?\d*/ }, { type: 'null' }],
    },
    employees: {
      title: 'Employees',
      description: 'should be empty or valid number',
      oneOf: [{ type: 'number', minimum: 0 }, { type: 'string', pattern: /\d*\.?\d*/ }, { type: 'null' }],
    },
  },
};

const FIELDS_TO_SPY = [
  'directorName',
  'recordOwnerId',
  'analystName',
  'recordSubOwnerId',
  'legalName',
  'abbrName',
  'dbaName',
  'web',
  'companyType',
  'businessModelId',
  'parentId',
  'phone',
  'address',
  'city',
  'state',
  'zip',
  'country.countryId',
  'country.countryName',
  'revenue',
  'employees',
  'tickerExchange',
  'tickerSymbol',
  'description',
];

const defaultState = fromJS(
  spy(
    {
      directorName: '',
      directorSuggests: [],
      recordOwnerId: null,
      analystName: '',
      analystSuggests: [],
      recordSubOwnerId: null,
      dealMakerId: null,
      isFetching: false,
      legalName: '',
      abbrName: '',
      dbaName: '',
      web: '',
      phone: null,
      address: null,
      tickerExchange: '',
      tickerSymbol: '',
      city: '',
      state: '',
      companyType: '',
      zip: '',
      country: {
        countryId: 0,
        countryName: '',
      },
      showCountry: false,
      isValidAddress: false,
      revenue: '',
      employees: '',
      description: '',
      states: states.map(state => ({ value: state, name: state })),
      parentId: null,
      entityTypes: entityTypes.map((type, i) => ({
        disabled: i === 0,
        value: type,
        name: type,
      })),
      error: null,
      inputErrors: {},
      isValid: true,
      parentCompany: spy(
        {
          suggestions: [],
          text: '',
          selected: null,
        },
        ['text'],
      ),
      businessModelId: 0,
    },
    FIELDS_TO_SPY,
  ),
);

const validator = getValidator(SCHEMA);

let wrappedReducer = handleApiError(ActionType.SAVE_COMPANY_ERROR, reducer);

wrappedReducer = check('directorName', 'directorSuggests', ActionType.LOADED_DIRECTORS, wrappedReducer);
wrappedReducer = check('analystName', 'analystSuggests', ActionType.LOADED_ANALYSTS, wrappedReducer);
wrappedReducer = autoNullId('directorName', 'recordOwnerId', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = autoNullId('analystName', 'recordSubOwnerId', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = resetFieldByOther('showCountry', 'zip', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = resetFieldByOther('showCountry', 'city', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = resetFieldByOther('showCountry', 'state', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = resetFieldByOther('showCountry', 'country.countryId', ActionType.UPDATE_FIELD, wrappedReducer);
wrappedReducer = resetFieldByOther('showCountry', 'country.countryName', ActionType.UPDATE_FIELD, wrappedReducer);

export default wrappedReducer;

function reducer(state = defaultState, action) {
  switch (action.type) {
    case ActionType.REVERT_FIELDS:
      return defaultState;

    case ActionType.UPDATE_FIELD:
      return checkValidity(setIn(state, action.name, action.value));

    case ActionType.UPDATE_PARENT_COMPANY: {
      if (action.info.suggestions && action.info.suggestions.length === 0) {
        state = state.setIn(['parentCompany', 'suggestions'], defaultState.getIn(['parentCompany', 'suggestions']));
      }

      if (typeof action.info.text !== 'undefined') {
        state = setIn(state, 'parentId', 0);
        state = state.update('parentCompany', state => setIn(state, 'text', action.info.text));
        state = state.update('parentCompany', state => setIn(state, 'selected', null));
        if (action.info.text === getOriginalIn(state, 'parentCompany.text')) {
          state = setIn(state, 'parentId', getOriginalIn(state, 'parentId'));
        }
      }

      if (action.info.selected) {
        state = setIn(state, 'parentId', action.info.selected.id);
        state = state.update('parentCompany', state => setIn(state, 'selected', fromJS(action.info.selected)));
      }

      return checkValidity(state);
    }

    case ActionType.SAVE_COMPANY:
      return state.set('isFetching', true);

    case ActionType.SAVE_COMPANY_SUCCESS:
      return defaultState.set('prevId', action.response.id);

    case ActionType.CLOSE_VALIDATION_ERROR:
      return state.deleteIn(['inputErrors', action.field]);

    case ActionType.LOADED_DIRECTORS:
      return state;

    case ActionType.LOADED_ANALYSTS:
      return state;

    case ActionType.FETCHING_LIST_COMPANY_SUCCESS:
      return state.setIn(['parentCompany', 'suggestions'], fromJS(action.response.data));

    default:
      return state;
  }
}

function checkValidity(state) {
  const plainState = toJS(state);
  const { showCountry } = plainState;

  // Skip phone validation if country is on
  if (showCountry) {
    plainState.phone = '111-111-1111';
  }

  const isValid = validator.isValid(plainState);
  const inputErrors = validator.getFirstError(plainState);

  return state.merge({
    inputErrors,
    isValid,
  });
}
